import React, { FunctionComponent, ReactElement } from 'react'
import { ButtonProps } from '@/types/Button.types'
import { getSharedProps } from '@/utils/button.utils'
import { ButtonState } from '@/app/_providers/ButtonProvider/ButtonProvider'

/**
 * Button component
 * @param {ButtonProps} props
 * @returns {React.ReactElement} React component
 */
export const Button: FunctionComponent<ButtonProps> = (props: ButtonProps): React.ReactElement<HTMLButtonElement> => {
  const {
    children,
    icon,
    type,
  } = props

  const sharedProps = getSharedProps(props)

  return (
    <button
      {...sharedProps}
      {...(type ? { type } : { type: 'button' })}
    >
      {icon}
      <span>
        {children}
      </span>
    </button>
  )
}

/**
 * This function returns a Button component based on the buttonState.
 * An optional additional action can be executed when the button is clicked.
 *
 * @param {ButtonState} buttonState - The current state of the button.
 * @param {Partial<ButtonProps>} buttonProps - The props to pass to the button.
 * @param {() => void} [additionalAction] - An optional additional action to execute on button click.
 *
 * @returns {ReactElement<HTMLButtonElement>}
 */
export const actionButtonVariations = (
  buttonState: ButtonState,
  buttonProps: Partial<ButtonProps>,
  additionalAction?: () => void,
): ReactElement<HTMLButtonElement> => {
  const {
    action,
    disabled,
    text,
  } = buttonState

  const wrappedAction = (): void => {
    if (additionalAction) {
      additionalAction()
    }
    action()
  }

  return (
    <Button
      {...buttonProps}
      onClick={wrappedAction}
      disabled={disabled}
    >
      {text}
    </Button>
  )
}
